import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    type NetworkQuestionnaireProgress,
    networkQuestionnaireProgressPath,
    useGetNetworkQuestionnaireProgress,
} from "src/api/client-api/network/useNetworkLists";
import type { AppSearch } from "src/api/_common/hydra/search";
import { EntityListLayout } from "src/components/Grid";
import { AutoGrid } from "src/components/Grid/layout/AutoGrid";
import { useGridColumns } from "src/components/Grid/hooks/columnsExtension/useGridColumns";
import { useColumnsTableAction } from "src/components/Grid/headers/useColumnsTableAction";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { NetworkOrganizationPanel } from "./NetworkOrganizationPanel";
import {
    NetworkEmptyState,
    NetworkError,
    type NetworkListProps,
    NetworkToolbar,
    tablePanelProps,
    toOptions,
    useNetworkExportAction,
} from "./networkListParts";
import { useNetworkOrganizationColumns } from "./useNetworkOrganizationColumns";
import { type NetworkListConfig, useNetworkListParams } from "./useNetworkListParams";

const GRID_KEY = "network-organizations-grid";

const SEARCH: AppSearch = {
    parentOrganization: { value: [] },
    organizationSector: { value: [] },
    ssqId: { value: [] },
    country: { value: [] },
    status: { value: "" },
};

const CONFIG: NetworkListConfig = {
    sortKeys: {
        organizationName: "organization.name",
        parentOrganization: "organization.parents",
        organizationSector: "organization.sectorName",
        ssqId: "questionnaire.title",
        completionRate: "progress.completionRate",
        answers: "progress.answers",
        status: "progress.status",
        lastLoginAt: "organization.lastLoginAt",
    },
    tagContext: "organization",
};

export const NetworkQuestionnaireProgressList = (props: NetworkListProps) => {
    const { filterOptions } = props;
    const { t } = useTranslation();
    const { selectedCompany, currentCampaign } = useFoodpilotContext();
    const [openedRow, setOpenedRow] = useState<NetworkQuestionnaireProgress | null>(null);

    const options = useMemo(
        () => ({
            organizations: toOptions(filterOptions.organizations),
            sectors: toOptions(filterOptions.sectors),
            questionnaires: toOptions(filterOptions.questionnaires),
            countries: filterOptions.countries.map((country) => ({ value: country, label: country })),
        }),
        [filterOptions],
    );
    const { params, filters, apiSort, pagination, search } = useNetworkListParams(CONFIG);
    const list = useGetNetworkQuestionnaireProgress({
        organizationId: selectedCompany?.id,
        params: { ...params, campaign: currentCampaign?.id },
    });

    const columns = useNetworkOrganizationColumns(options, setOpenedRow);
    const columnsConfig = useGridColumns(columns, GRID_KEY);
    const columnsAction = useColumnsTableAction(columnsConfig);
    const exportAction = useNetworkExportAction({
        path: networkQuestionnaireProgressPath,
        fileNameStem: "network-organizations",
        filters,
        sort: apiSort,
        search: search.delayedSearch,
        campaignId: currentCampaign?.id,
        totalItems: list.totalItems,
        entityLabel: t("network.export.questionnaires", "questionnaires"),
    });

    return (
        <EntityListLayout
            appearance="hub"
            ariaLabel={t("network.tabs.questionnaires", "Questionnaires")}
            gridHeader={
                <NetworkToolbar
                    search={search}
                    placeholder={t("network.organizations.search", "Search an organization")}
                    actions={[columnsAction, exportAction]}
                />
            }
        >
            {list.errorStatus !== null ?
                <NetworkError status={list.errorStatus} onRetry={list.refetch} />
            :   <AutoGrid<NetworkQuestionnaireProgress>
                    appearance="hub"
                    mobileTableMode="scroll"
                    gridKey={GRID_KEY}
                    columns={columns}
                    columnsConfig={columnsConfig}
                    data={list.rows}
                    totalItems={list.totalItems}
                    isLoading={!list.isReady || list.isRefetching}
                    pagination={pagination}
                    searchParams={SEARCH}
                    enableRowActions={false}
                    tablePanelProps={tablePanelProps("organizations")}
                    onRowClick={setOpenedRow}
                    renderEmptyState={({ resetFilters }) => (
                        <NetworkEmptyState
                            hasCriteria={Object.keys(filters).length > 0 || search.search !== ""}
                            onReset={() => {
                                resetFilters();
                                search.setSearch("");
                            }}
                        />
                    )}
                />
            }
            <NetworkOrganizationPanel row={openedRow} onClose={() => setOpenedRow(null)} />
        </EntityListLayout>
    );
};
